import { useState, useEffect, useCallback } from 'react';

// Cache globale per i documenti
const documentCache = new Map();
const loadingStates = new Map();

export const useDocumentCache = () => {
    const [cache, setCache] = useState(documentCache);

    const getDocument = useCallback(async (tenantId, docType) => {
        const cacheKey = `${tenantId}-${docType}`;

        // Se è già in cache, ritorna subito
        if (documentCache.has(cacheKey)) {
            return documentCache.get(cacheKey);
        }

        // Se sta già caricando, aspetta
        if (loadingStates.has(cacheKey)) {
            return loadingStates.get(cacheKey);
        }

        // Crea una promise per il caricamento
        const loadPromise = fetch(`/api/public/tenants/${tenantId}/documents`)
            .then(res => res.json())
            .then(docs => {
                const doc = docs
                    .filter(d => d.docType === docType)
                    .sort((a, b) => {
                        const aTime = new Date(a.updatedAt || a.createdAt).getTime();
                        const bTime = new Date(b.updatedAt || b.createdAt).getTime();
                        return bTime - aTime || b.id - a.id;
                    })[0];

                // Salva in cache
                documentCache.set(cacheKey, doc);
                loadingStates.delete(cacheKey);
                setCache(new Map(documentCache));

                return doc;
            })
            .catch(error => {
                loadingStates.delete(cacheKey);
                throw error;
            });

        loadingStates.set(cacheKey, loadPromise);
        return loadPromise;
    }, []);

    const clearCache = useCallback((tenantId, docType) => {
        const cacheKey = docType ? `${tenantId}-${docType}` : null;

        if (cacheKey) {
            documentCache.delete(cacheKey);
            loadingStates.delete(cacheKey);
        } else {
            // Clear all cache for tenant
            for (const key of documentCache.keys()) {
                if (key.startsWith(`${tenantId}-`)) {
                    documentCache.delete(key);
                    loadingStates.delete(key);
                }
            }
        }
        setCache(new Map(documentCache));
    }, []);

    const prefetchDocument = useCallback(async (tenantId, docType) => {
        try {
            await getDocument(tenantId, docType);
        } catch (error) {
            console.warn('Prefetch failed for', docType, error);
        }
    }, [getDocument]);

    return { getDocument, clearCache, prefetchDocument, cache };
};